import { useEffect, useState } from "react";
import { ShieldCheck, Trash2, UserPlus, Eye, EyeOff, ArrowUpCircle, ArrowDownCircle, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#6B1226]/15 rounded-xl px-4 py-3 text-base outline-none focus:border-[#6B1226]/50 transition-colors";

export function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", password: "", role: "admin" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ firstName: "", lastName: "", email: "", phone: "", password: "" });
  const [showEditPw, setShowEditPw] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState("");

  const load = () => api.get("/admin/users").then((res) => setUsers(res.data)).catch(() => setUsers([]));

  useEffect(() => {
    load();
  }, []);

  const startEdit = (u) => {
    setEditing(u.id);
    setEditError("");
    setShowEditPw(false);
    const parts = (u.name || "").split(" ");
    setEditForm({
      firstName: u.first_name || parts[0] || "",
      lastName: u.last_name || parts.slice(1).join(" ") || "",
      email: u.email || "",
      phone: u.phone || "",
      password: "",
    });
  };

  const saveEdit = async (u) => {
    setEditBusy(true);
    setEditError("");
    try {
      await api.put(`/admin/users/${u.id}`, {
        first_name: editForm.firstName,
        last_name: editForm.lastName,
        email: editForm.email,
        phone: editForm.phone,
        password: editForm.password || undefined,
      });
      toast.success(`Compte de ${editForm.firstName} ${editForm.lastName} mis à jour`);
      setEditing(null);
      load();
    } catch (err) {
      setEditError(formatApiError(err));
    } finally {
      setEditBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/admin/users", {
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        password: form.password,
        role: form.role,
      });
      toast.success(`Compte ${form.role === "admin" ? "administrateur" : "client"} créé pour ${form.firstName}`);
      setForm({ firstName: "", lastName: "", email: "", password: "", role: "admin" });
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleRole = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { role: u.role === "admin" ? "client" : "admin" });
      toast.success(u.role === "admin" ? `${u.name} est maintenant client` : `${u.name} est maintenant administrateur`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Supprimer définitivement le compte de ${u.name} ?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Compte supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="mt-8 space-y-6" data-testid="admin-users">
      <form onSubmit={submit} className="bg-white rounded-2xl border border-[#6B1226]/10 p-5 space-y-3" data-testid="admin-user-form">
        <p className="font-display text-xl font-semibold text-[#1C1917] flex items-center gap-2">
          <UserPlus size={18} className="text-[#6B1226]" /> Créer un compte
        </p>
        <div className="grid grid-cols-2 gap-2.5">
          <input required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} placeholder="Prénom" className={inputCls} data-testid="admin-user-firstname" />
          <input required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} placeholder="Nom" className={inputCls} data-testid="admin-user-lastname" />
        </div>
        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={inputCls} data-testid="admin-user-email" />
        <div className="relative">
          <input required type={showPw ? "text" : "password"} minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mot de passe (6 caractères min.)" className={`${inputCls} pr-11`} data-testid="admin-user-password" />
          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B1226]/50 hover:text-[#6B1226] transition-colors"
            data-testid="admin-user-password-toggle"
            aria-label={showPw ? "Masquer le mot de passe" : "Afficher le mot de passe"}
          >
            {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
          </button>
        </div>
        <div className="flex gap-2">
          {[
            { id: "admin", label: "Administrateur" },
            { id: "client", label: "Client" },
          ].map((r) => (
            <button
              type="button"
              key={r.id}
              onClick={() => setForm({ ...form, role: r.id })}
              className={`flex-1 rounded-full px-4 py-2.5 text-xs font-semibold tracking-wide transition-colors ${
                form.role === r.id ? "bg-[#6B1226] text-[#FAF6F0]" : "bg-white border border-[#6B1226]/15 text-[#57534E]"
              }`}
              data-testid={`admin-user-role-${r.id}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        {error && (
          <p className="text-sm text-[#6B1226] bg-[#6B1226]/5 border border-[#6B1226]/20 rounded-xl px-4 py-3" data-testid="admin-user-error">
            {error}
          </p>
        )}
        <button type="submit" disabled={busy} className="w-full rounded-full bg-[#6B1226] text-[#FAF6F0] py-3.5 text-sm font-bold hover:bg-[#580E1D] transition-colors disabled:opacity-60" data-testid="admin-user-create-button">
          {busy ? "Création…" : "Créer le compte"}
        </button>
      </form>

      <div>
        <p className="eyebrow mb-3">Comptes existants</p>
        {users === null ? (
          <p className="text-sm text-[#57534E] italic">Chargement…</p>
        ) : (
          <ul className="space-y-3" data-testid="admin-users-list">
            {users.map((u) => (
              <li key={u.id} className="bg-white rounded-2xl border border-[#6B1226]/10 p-4" data-testid="admin-user-row">
                <div className="flex items-center gap-3">
                  <span className={`w-11 h-11 rounded-full flex items-center justify-center font-display text-lg font-semibold shrink-0 ${u.role === "admin" ? "bg-[#6B1226] text-[#FAF6F0]" : "bg-[#F5EEDC] text-[#8C6D23]"}`}>
                    {(u.first_name || u.name || " ").charAt(0).toUpperCase()}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-semibold text-sm text-[#1C1917] truncate">{u.name}</span>
                    <span className="block text-xs text-[#78716C] truncate">{u.email}</span>
                  </span>
                  {u.role === "admin" && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#6B1226]/10 text-[#6B1226] px-2.5 py-1 text-[0.6rem] font-bold tracking-wide uppercase shrink-0" data-testid="admin-user-role-badge">
                      <ShieldCheck size={11} /> Admin
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => (editing === u.id ? setEditing(null) : startEdit(u))}
                      className="inline-flex items-center gap-1 rounded-full border border-[#6B1226]/25 text-[#6B1226] px-3 py-1.5 text-[0.65rem] font-bold hover:bg-[#6B1226]/5 transition-colors"
                      data-testid="admin-user-edit"
                    >
                      {editing === u.id ? (<><X size={13} /> Fermer</>) : (<><Pencil size={13} /> Modifier</>)}
                    </button>
                    {u.id !== me?.id && (
                      <>
                        <button
                          onClick={() => toggleRole(u)}
                          className="inline-flex items-center gap-1 rounded-full border border-[#C5A059]/60 text-[#8C6D23] px-3 py-1.5 text-[0.65rem] font-bold hover:bg-[#F5EEDC] transition-colors"
                          data-testid="admin-user-toggle-role"
                        >
                          {u.role === "admin" ? (
                            <><ArrowDownCircle size={13} /> Passer client</>
                          ) : (
                            <><ArrowUpCircle size={13} /> Passer admin</>
                          )}
                        </button>
                        <button
                          onClick={() => remove(u)}
                          className="p-2 rounded-full text-stone-400 hover:text-[#6B1226] hover:bg-[#6B1226]/5 transition-colors"
                          title="Supprimer le compte"
                          data-testid="admin-user-delete"
                        >
                          <Trash2 size={17} />
                        </button>
                      </>
                    )}
                  </span>
                </div>
                {editing === u.id && (
                  <div className="mt-4 pt-4 border-t border-[#6B1226]/10 space-y-3" data-testid="admin-user-edit-form">
                    <div className="grid grid-cols-2 gap-2.5">
                      <input required value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} placeholder="Prénom" className={inputCls} data-testid="admin-user-edit-firstname" />
                      <input required value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} placeholder="Nom" className={inputCls} data-testid="admin-user-edit-lastname" />
                    </div>
                    <input required type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} placeholder="Email" className={inputCls} data-testid="admin-user-edit-email" />
                    <input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} placeholder="Téléphone" className={inputCls} data-testid="admin-user-edit-phone" />
                    <div className="relative">
                      <input type={showEditPw ? "text" : "password"} minLength={6} value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Nouveau mot de passe (laisser vide pour ne pas changer)" className={`${inputCls} pr-11`} data-testid="admin-user-edit-password" />
                      <button
                        type="button"
                        onClick={() => setShowEditPw((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B1226]/50 hover:text-[#6B1226] transition-colors"
                        data-testid="admin-user-edit-password-toggle"
                        aria-label={showEditPw ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                      >
                        {showEditPw ? <EyeOff size={17} /> : <Eye size={17} />}
                      </button>
                    </div>
                    {editError && (
                      <p className="text-sm text-[#6B1226] bg-[#6B1226]/5 border border-[#6B1226]/20 rounded-xl px-4 py-3" data-testid="admin-user-edit-error">
                        {editError}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <button onClick={() => setEditing(null)} className="flex-1 rounded-full border border-[#6B1226]/25 text-[#6B1226] py-3 text-sm font-bold hover:bg-[#6B1226]/5 transition-colors" data-testid="admin-user-edit-cancel">
                        Annuler
                      </button>
                      <button
                        onClick={() => saveEdit(u)}
                        disabled={editBusy || !editForm.firstName.trim() || !editForm.lastName.trim() || !editForm.email.trim()}
                        className="flex-1 rounded-full bg-[#6B1226] text-[#FAF6F0] py-3 text-sm font-bold hover:bg-[#580E1D] transition-colors disabled:opacity-60"
                        data-testid="admin-user-edit-save"
                      >
                        {editBusy ? "Enregistrement…" : "Enregistrer"}
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
